import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Observable } from 'rxjs';
import authorizeService from '../components/api-authorization/AuthorizeService';
import { DevBarInfo } from '../components/DevBar/DevBar';
import RolesHelper from '../components/Helpers/RolesHelper';
import { ApplicationRoutes, FeatureFlags, LocalStorageKeys } from '../constants';
import { useObservableWithSubject } from '../hooks/UseObservable';
import SystemApiService from './api/SystemApiService';

interface GeneralContextType {
  initializeActiveUser: () => Promise<void>;
  initializeFeatureFlags: () => Promise<void>;
  triggerEasterEgg: (name: string) => void;
  triggerAppRefresh: () => void;
  triggerDevBarUpdate: (data: DevBarInfo) => void;
  checkFeatureFlag: (featureFlagId: string) => boolean;
  checkUserRole: (userRole: string) => boolean;
  getCachedData: (dataKey: string, shouldStoreDataInLocalStorage: boolean) => unknown;
  setCachedData: (dataKey: string, shouldStoreDataInLocalStorage: boolean, value: unknown) => void;
  clearCachedData: (dataKey: string) => void;
  easterEggToTrigger$: Observable<string>;
  refreshAppTrigger$: Observable<void>;
  updateDevBarData$: Observable<DevBarInfo>;
  // The active user data, available to any component in the app
  activeUserViewModel: User_BasicDetails_ViewModel | null;
}

// A general context for use as a general data store / service class
const GeneralContext = createContext<GeneralContextType | undefined>(undefined);

const GeneralContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();

  // A ref object to hold generic data of any type in memory
  const cachedDataRef = useRef<{ [index: string]: unknown }>({});

  // Feature flags and their availability
  const [featureFlagsAndValues, setFeatureFlagsAndValues] = useState<{ [index: string]: boolean }>({});

  const [activeUserViewModel, setActiveUserViewModel] = useState<User_BasicDetails_ViewModel | null>(null);

  // The current user's roles
  const [userRoles, setUserRoles] = useState<string[]>([]);

  const [easterEggToTrigger$, setEasterEggToTrigger] = useObservableWithSubject<string>();
  const [refreshAppTrigger$, setRefreshAppTrigger] = useObservableWithSubject<void>();
  const [updateDevBarData$, setUpdateDevBarData] = useObservableWithSubject<DevBarInfo>();

  const urlHistory = useRef<string[]>([]);

  useEffect(() => {
    const currUrl = location.pathname + location.search + location.hash;

    // Don't put the same URL on the queue twice
    if (urlHistory.current.length && urlHistory.current[urlHistory.current.length - 1] === currUrl) {
      return;
    }

    // Ignore authentication or register URL's
    if (
      currUrl.toLowerCase().includes(ApplicationRoutes.AuthenticationRoutes.Root.toLowerCase()) ||
      currUrl.toLowerCase().includes(ApplicationRoutes.Register.toLowerCase())
    ) {
      return;
    }

    urlHistory.current.push(currUrl);

    const last2Urls = urlHistory.current.length > 2 ? urlHistory.current.slice(urlHistory.current.length - 2) : urlHistory.current;
    localStorage.setItem(LocalStorageKeys.General.LastUrls, JSON.stringify(last2Urls));
  }, [location]);

  const initializeActiveUser = useCallback(async () => {
    try {
      const result = await authorizeService.populateActiveUser();
      setActiveUserViewModel(result);
    } catch (error) {}
  }, []);

  const initializeFeatureFlags = useCallback(async () => {
    try {
      const roles = await RolesHelper.getRoles();

      setUserRoles(roles);

      const featureFlagsToGet = Object.values(FeatureFlags);

      const results = await Promise.all(
        featureFlagsToGet.map(async (v) => {
          const result = await SystemApiService.getFeatureFlag(v);

          return {
            id: v,
            result
          };
        })
      );

      const newObj: { [index: string]: boolean } = {};

      results.forEach((currResult) => {
        newObj[currResult.id] = currResult.result;
      });

      setFeatureFlagsAndValues(newObj);
    } catch (error) {}
  }, []);

  const triggerEasterEgg = useCallback(
    (name: string) => {
      setEasterEggToTrigger(name);
    },
    [setEasterEggToTrigger]
  );

  const triggerAppRefresh = useCallback(() => {
    setRefreshAppTrigger();
  }, [setRefreshAppTrigger]);

  const triggerDevBarUpdate = useCallback(
    (data: DevBarInfo) => {
      setUpdateDevBarData(data);
    },
    [setUpdateDevBarData]
  );

  const checkFeatureFlag = useCallback(
    (featureFlagId: string): boolean => {
      return featureFlagsAndValues[featureFlagId] ?? false;
    },
    [featureFlagsAndValues]
  );

  const checkUserRole = useCallback(
    (userRole: string): boolean => {
      return userRoles.includes(userRole);
    },
    [userRoles]
  );

  const getCachedData = (dataKey: string, shouldStoreDataInLocalStorage: boolean): unknown => {
    // If the value is in memory, use that
    const dataFromMemory = cachedDataRef.current[dataKey];

    // If we have a value from memory, write it to the local storage if we need to,
    // overwriting any existing data
    if (dataFromMemory && shouldStoreDataInLocalStorage) {
      localStorage.setItem(dataKey, JSON.stringify(dataFromMemory));
    }

    if (dataFromMemory) {
      return dataFromMemory;
    }

    // If we didn't have the data in memory, check local storage, and write it to memory if it's found
    if (shouldStoreDataInLocalStorage) {
      const dataFromLocalStorage = localStorage.getItem(dataKey);
      if (dataFromLocalStorage) {
        const parsedData = JSON.parse(dataFromLocalStorage);
        cachedDataRef.current[dataKey] = parsedData;
        return parsedData;
      }
    }

    return null;
  };

  const setCachedData = (dataKey: string, shouldStoreDataInLocalStorage: boolean, value: unknown) => {
    cachedDataRef.current[dataKey] = value;

    if (shouldStoreDataInLocalStorage) {
      localStorage.setItem(dataKey, JSON.stringify(value));
    }
  };

  const clearCachedData = (dataKey: string) => {
    if (cachedDataRef.current[dataKey]) {
      delete cachedDataRef.current[dataKey];
    }

    localStorage.removeItem(dataKey);
  };

  return (
    <GeneralContext.Provider
      value={{
        triggerEasterEgg,
        triggerAppRefresh,
        triggerDevBarUpdate,
        checkFeatureFlag,
        checkUserRole,
        initializeFeatureFlags,
        initializeActiveUser,
        getCachedData,
        setCachedData,
        clearCachedData,
        easterEggToTrigger$,
        refreshAppTrigger$,
        updateDevBarData$,
        activeUserViewModel
      }}
    >
      {children}
    </GeneralContext.Provider>
  );
};

const useGeneralContext = () => {
  const context = useContext(GeneralContext);

  if (!context) {
    throw new Error('useGeneralContext() must be used within an GeneralContext. Please verify your DOM structure.');
  }
  return context;
};

export { GeneralContextProvider, useGeneralContext };
